// src/pages/MeetingRoomPage.jsx

import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Tabs,
  Tab,
  Button,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  Divider,
  Paper,
  Stack,
  IconButton,
  useTheme,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { useSnackbar } from "notistack";
import BookingForm from "../components/BookingForm";
import MeetingRoomCalendarStyled from "../components/MeetingRoomCalendarStyled";
import { getRoomById } from "../services/roomService";
import { getRoomFields } from "../services/roomFieldService";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Image as ImageIcon } from '@mui/icons-material';

// Styled Components
const PageContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(0, 2),
}));

const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
    borderRadius: '2px 2px 0 0',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '1rem',
  padding: theme.spacing(1, 2),
  minHeight: 48,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
}));

const TabPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0),
}));

const AboutCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: 'none', // Remove shadow
}));

const RoomImage = styled(CardMedia)(({ theme }) => ({
  height: 300,
  objectFit: 'cover',
  borderRadius: `${theme.shape.borderRadius * 2}px ${theme.shape.borderRadius * 2}px 0 0`,
  [theme.breakpoints.down('md')]: {
    height: 200,
  },
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(1),
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// Skeleton Loading Component
const MeetingRoomPageSkeleton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <PageContainer maxWidth={false} sx={{ maxWidth: 1300 }}>
      {/* Page Header Skeleton */}
      <PageHeader>
        <Skeleton 
          variant="circular" 
          width={32} 
          height={32} 
          sx={{ flexShrink: 0 }}
        />
        <Box sx={{ flexGrow: 1 }}>
          <Skeleton 
            variant="text" 
            width="40%" 
            height={40} 
            sx={{ fontSize: '2rem' }}
          />
          <Skeleton 
            variant="text" 
            width="60%" 
            height={20} 
            sx={{ mt: 0.5 }}
          />
        </Box>
      </PageHeader>

      {/* Tab Navigation Skeleton */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Stack 
          direction="row" 
          spacing={isMobile ? 1 : 3}
          sx={{ 
            px: isMobile ? 1 : 0,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' }
          }}
        >
          {[1, 2, 3].map((_, index) => (
            <Skeleton 
              key={index}
              variant="rectangular" 
              width={isMobile ? 120 : 140} 
              height={48}
              sx={{ 
                borderRadius: 1,
                flexShrink: 0
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Content Skeleton */}
      <Grid container spacing={3}>
        {/* Left Column - Room Image and Info */}
        <Grid item xs={12} md={6}>
          <AboutCard>
            {/* Room Image Skeleton */}
            <Skeleton 
              variant="rectangular" 
              width="100%" 
              height={isMobile ? 200 : 300}
              sx={{ 
                borderRadius: `${theme.shape.borderRadius * 2}px ${theme.shape.borderRadius * 2}px 0 0`
              }}
            />
            
            <CardContent>
              <Skeleton 
                variant="text" 
                width="50%" 
                height={32} 
                sx={{ mb: 2 }}
              />
              
              {/* Room Fields Skeleton */}
              <Stack spacing={2}>
                {[1, 2, 3, 4].map((_, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="circular" width={24} height={24} />
                    <Skeleton variant="text" width="70%" height={24} />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </AboutCard>
        </Grid>

        {/* Right Column - Room Details */}
        <Grid item xs={12} md={6}>
          <AboutCard>
            <CardContent>
              <Skeleton 
                variant="text" 
                width="40%" 
                height={32} 
                sx={{ mb: 2 }}
              />
              
              <Stack spacing={2}>
                {[1, 2, 3].map((_, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="circular" width={24} height={24} />
                    <Skeleton variant="text" width="80%" height={24} />
                  </Box>
                ))}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Skeleton 
                variant="text" 
                width="50%" 
                height={32} 
                sx={{ mb: 2 }}
              />
              
              <Stack spacing={2}>
                {[1, 2].map((_, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="circular" width={24} height={24} />
                    <Skeleton variant="text" width="60%" height={24} />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </AboutCard>
        </Grid>
      </Grid>

      {/* Additional Content Skeleton */}
      <Box sx={{ mt: 3 }}>
        <Skeleton 
          variant="rectangular" 
          width="100%" 
          height={200}
          sx={{ borderRadius: 2 }}
        />
      </Box>
    </PageContainer>
  );
};

function MeetingRoomPage() {
  const { roomId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State management
  const [room, setRoom] = useState(null);
  const [roomFields, setRoomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);

  // Tab configuration
  const tabs = [
    { label: "Booking", icon: <CalendarTodayIcon />, value: "booking" },
    { label: "Room Calendar", icon: <EventIcon />, value: "calendar" },
    { label: "About", icon: <InfoIcon />, value: "about" }
  ];

  // Get tab index from URL hash
  const getTabFromHash = (hash) => {
    const value = hash.replace('#', '');
    const tabIndex = tabs.findIndex(tab => tab.value === value);
    return tabIndex >= 0 ? tabIndex : 0;
  };

  // Set URL hash from tab index
  const setHashFromTab = (tabIndex) => {
    const value = tabs[tabIndex]?.value || tabs[0].value;
    navigate(`#${value}`, { replace: true });
  };

  // Handle URL hash changes and set initial tab
  useEffect(() => {
    const tabIndex = getTabFromHash(location.hash);
    setCurrentTab(tabIndex);
    
    // Set default hash if none exists
    if (!location.hash) {
      setHashFromTab(0);
    }
  }, [location.hash]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setHashFromTab(newValue);
  };

  // Booking handlers
  const handleBookingSuccess = (msg) => {
    enqueueSnackbar(msg, { variant: "success" });
    setRefreshKey((prev) => prev + 1);
  };

  const handleBookingError = (msg) => {
    enqueueSnackbar(msg, { variant: "error" });
  };

  // Load room data and fields
  useEffect(() => {
    const loadRoomData = async () => {
      try {
        setLoading(true);
        const [roomData, fieldsData] = await Promise.all([
          getRoomById(roomId),
          getRoomFields().catch(() => []) // Don't fail if fields can't be loaded
        ]);
        
        setRoom(roomData);
        setRoomFields(fieldsData || []);
      } catch (err) {
        console.error("Error fetching room data:", err);
        enqueueSnackbar("Failed to load room data", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };

    if (roomId) {
      loadRoomData();
    }
  }, [roomId, enqueueSnackbar]);

  // Render field value helper
  const renderFieldValue = (field, value) => {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    switch (field.type) {
      case 'boolean':
        return value === true || value === 'true' ? 'Yes' : 'No';
      case 'select':
        return value;
      case 'number':
        return value.toString();
      default:
        return value;
    }
  };

  // Render room fields
  const renderRoomFields = () => {
    if (!roomFields.length) {
      // Fallback for when no fields are configured
      return (
        <Stack spacing={2}>
          {room.capacity && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon color="primary" fontSize="small" />
              <Typography variant="body1">
                <strong>Capacity:</strong> {room.capacity} people
              </Typography>
            </Box>
          )}
          {room.location && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon color="primary" fontSize="small" />
              <Typography variant="body1">
                <strong>Location:</strong> {room.location}
              </Typography>
            </Box>
          )}
        </Stack>
      );
    }

    return (
      <Stack spacing={2}>
        {roomFields
          .sort((a, b) => a.order - b.order)
          .map(field => {
            let value;
            
            // Handle system fields
            if (field.isSystem) {
              if (field.name === 'location') {
                value = room.location;
              } else if (field.name === 'capacity') {
                value = room.capacity;
              }
            } else {
              // Handle custom fields
              const customFields = room.customFields instanceof Map 
                ? Object.fromEntries(room.customFields)
                : room.customFields;
              value = customFields?.[field.name];
            }

            const displayValue = renderFieldValue(field, value);
            
            if (!displayValue) return null;

            // Get appropriate icon
            const getFieldIcon = () => {
              if (field.name === 'capacity') return <PeopleIcon color="primary" fontSize="small" />;
              if (field.name === 'location') return <LocationOnIcon color="primary" fontSize="small" />;
              return <InfoIcon color="primary" fontSize="small" />;
            };

            return (
              <Box key={field._id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getFieldIcon()}
                <Typography variant="body1">
                  <strong>{field.label}:</strong> {displayValue}
                </Typography>
              </Box>
            );
          })
          .filter(Boolean)}
      </Stack>
    );
  };

  // Go back handler
  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return <MeetingRoomPageSkeleton />;
  }

  if (!room) {
    return (
      <PageContainer>
        <Typography color="error">Room not found</Typography>
      </PageContainer>
    );
  }

  const roomImage = room.images && room.images.length > 0 ? room.images[0] : null;

  return (
    <PageContainer maxWidth={false} sx={{ maxWidth: 1300 }}>
      {/* Page Header */}
      <PageHeader>
        <BackButton onClick={handleGoBack} size="small">
          <ArrowBackIcon />
        </BackButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {room.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Meeting Room Details & Booking
          </Typography>
        </Box>
      </PageHeader>

      {/* Tab Navigation */}
      <StyledTabs 
        value={currentTab} 
        onChange={handleTabChange}
        variant={isMobile ? "scrollable" : "standard"}
        scrollButtons="auto"
      >
        {tabs.map((tab, index) => (
          <StyledTab
            key={tab.value}
            label={tab.label}
            icon={tab.icon}
            iconPosition="start"
            sx={{ 
              minWidth: isMobile ? 'auto' : 140,
              px: isMobile ? 1 : 2,
            }}
          />
        ))}
      </StyledTabs>

      {/* Tab Panels */}
      
      {/* Booking Tab */}
      <TabPanel hidden={currentTab !== 0}>
        <BookingForm
          roomId={roomId}
          onBookingSuccess={handleBookingSuccess}
          onBookingError={handleBookingError}
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
        />
      </TabPanel>

      {/* Room Calendar Tab */}
      <TabPanel hidden={currentTab !== 1}>
        <MeetingRoomCalendarStyled
          roomId={roomId}
          refreshKey={refreshKey}
          chosenDate={selectedDate}
        />
      </TabPanel>

      {/* About Tab */}
      <TabPanel hidden={currentTab !== 2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <AboutCard>
              {roomImage ? (
                <RoomImage
                  image={roomImage.url || roomImage.thumbnailUrl}
                  title={room.name}
                />
              ) : (
                <Box
                  sx={{
                    height: 300,
                    backgroundColor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'grey.400',
                    borderRadius: `${theme.shape.borderRadius * 2}px ${theme.shape.borderRadius * 2}px 0 0`,
                  }}
                >
                  <ImageIcon sx={{ fontSize: 64 }} />
                </Box>
              )}
            </AboutCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <AboutCard>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Room Information
                </Typography>
                {renderRoomFields()}

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Booking Information
                </Typography>
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Use the <strong>Booking</strong> tab to reserve this room for your meetings.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View all bookings and availability in the <strong>Room Calendar</strong> tab.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setCurrentTab(0)}
                      startIcon={<CalendarTodayIcon />}
                    >
                      Book Room
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setCurrentTab(1)}
                      startIcon={<EventIcon />}
                    >
                      View Calendar
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </AboutCard>
          </Grid>
        </Grid>
      </TabPanel>
    </PageContainer>
  );
}

export default MeetingRoomPage;